import { useState } from "react";
import { checkpoints } from "../constants";
import { useColors } from "../ColorsContext";

const PATH_VIDEOS = {
  puzzle: "/videos/Puzzle_Path_video.mp4",
  action: "/videos/Action_Path_video.mp4",
  gamer:  "/videos/Hero_Path_video.mp4",
};

const OBSTACLE_EMOJIS = ["🧗", "🪜", "🔩", "🏋️", "🧱", "🏖️"];

const PATH_AVATARS = {
  gamer: {
    options: [
      "/avatar_pics/Hero Path/Wizard.png",
      "/avatar_pics/Hero Path/Archer.png",
      "/avatar_pics/Hero Path/Warrior.png",
      "/avatar_pics/Hero Path/Paladin.png",
      "/avatar_pics/Hero Path/Rouge.png",
      "/avatar_pics/Hero Path/Bard.png",
    ],
    defaults: [
      "/avatar_pics/Hero Path/Wizard.png",
      "/avatar_pics/Hero Path/Archer.png",
      "/avatar_pics/Hero Path/Warrior.png",
      "/avatar_pics/Hero Path/Paladin.png",
    ],
  },
  puzzle: {
    options: [
      "/avatar_pics/Puzzle Path/Analyst.png",
      "/avatar_pics/Puzzle Path/Crypto.png",
      "/avatar_pics/Puzzle Path/Field Agent.png",
      "/avatar_pics/Puzzle Path/Ghost.png",
      "/avatar_pics/Puzzle Path/Hacker.png",
      "/avatar_pics/Puzzle Path/Strategist.png",
    ],
    defaults: [
      "/avatar_pics/Puzzle Path/Analyst.png",
      "/avatar_pics/Puzzle Path/Crypto.png",
      "/avatar_pics/Puzzle Path/Field Agent.png",
      "/avatar_pics/Puzzle Path/Ghost.png",
    ],
  },
  action: {
    options: [
      "/avatar_pics/Action Path/Commander.png",
      "/avatar_pics/Action Path/Engineer.png",
      "/avatar_pics/Action Path/Medic.png",
      "/avatar_pics/Action Path/Rookie.png",
      "/avatar_pics/Action Path/Runner.png",
      "/avatar_pics/Action Path/Scout.png",
    ],
    defaults: [
      "/avatar_pics/Action Path/Commander.png",
      "/avatar_pics/Action Path/Engineer.png",
      "/avatar_pics/Action Path/Medic.png",
      "/avatar_pics/Action Path/Rookie.png",
    ],
  },
};

function avatarName(src) {
  return src.split("/").pop().replace(".png", "");
}

export default function ScreenBrief({ path, onNext, onBack, layout }) {
  const C = useColors();
  const { scale = 1, offsetX = 0, vpWidth = 390 } = layout || {};
  const extraLeft = offsetX / scale;
  const edgeStyle = {
    marginLeft: -extraLeft, width: vpWidth / scale,
    paddingLeft: extraLeft + 16, paddingRight: extraLeft + 16,
  };
  const [step, setStep] = useState(0);
  const [avatars, setAvatars] = useState([...PATH_AVATARS[path.id].defaults]);
  const [activeSlot, setActiveSlot] = useState(null);

  function selectAvatar(slotIndex, emoji) {
    const next = [...avatars];
    next[slotIndex] = emoji;
    setAvatars(next);
    setActiveSlot(null);
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: C.navy }}>
      {/* Coloured header */}
      <div style={{
        background: path.color, paddingTop: 10, paddingBottom: 10,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0, ...edgeStyle,
      }}>
        <div style={{ fontWeight: 800, color: "#fff", fontSize: 14 }}>{path.label.toUpperCase()}</div>
        <div style={{ fontSize: 16 }}>{path.icon}</div>
      </div>

      {/* Path video — autoplay, looping */}
      <video
        key={path.id}
        src={PATH_VIDEOS[path.id]}
        autoPlay
        loop
        muted
        playsInline
        style={{ width: "100%", height: 120, objectFit: "cover", objectPosition: "top", flexShrink: 0 }}
      />

      {/* ── STEP 0: Narrative + team roster ── */}
      {step === 0 && (
        <div style={{ flex: 1, overflow: "auto", padding: 18, display: "flex", flexDirection: "column" }}>
          {/* Narrative card */}
          <div style={{
            background: C.card, borderRadius: 10, padding: 12,
            border: `1px solid ${path.color}44`, marginBottom: 12, flexShrink: 0,
          }}>
            <div style={{ fontSize: 11, color: path.color, letterSpacing: 2, marginBottom: 8 }}>
              CHALLENGE NARRATIVE
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.white, marginBottom: 10 }}>
              {path.narrative}
            </div>
            <div style={{ fontSize: 13, color: C.light, lineHeight: 1.7 }}>
              {path.mission}
            </div>
          </div>

          <div style={{ fontSize: 12, color: C.muted, letterSpacing: 2, marginBottom: 10, flexShrink: 0 }}>YOUR TEAM</div>

          {/* Player slots */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, flex: 1, minHeight: 100, alignItems: "stretch" }}>
            {avatars.map((src, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column" }}>
                <div
                  onClick={() => setActiveSlot(activeSlot === i ? null : i)}
                  style={{
                    position: "relative", borderRadius: 10, overflow: "hidden",
                    border: `2px solid ${activeSlot === i ? path.color : C.dim}`,
                    cursor: "pointer", transition: "border-color 0.2s",
                    flex: 1, minHeight: 100,
                  }}
                >
                  <img
                    src={src} alt={avatarName(src)}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  {/* Text overlay */}
                  <div style={{
                    position: "absolute", bottom: 18, left: 0, right: 0,
                    background: "linear-gradient(transparent, rgba(0,0,0,0.75))",
                    padding: "2px 4px 5px",
                    textAlign: "center",
                  }}>
                    <div style={{ fontSize: 9, color: "#fff", fontWeight: 700 }}>P{i + 1} · {avatarName(src)}</div>
                    <div style={{ fontSize: 7, color: "rgba(255,255,255,0.7)", marginTop: 1 }}>tap to change</div>
                  </div>
                </div>

                {/* Inline avatar picker */}
                {activeSlot === i && (
                  <div style={{
                    marginTop: 6, background: C.navyMid, borderRadius: 8,
                    padding: 8, border: `1px solid ${path.color}44`,
                    display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6,
                  }}>
                    {PATH_AVATARS[path.id].options.map(av => (
                      <button
                        key={av}
                        onClick={() => selectAvatar(i, av)}
                        style={{
                          background: avatars[i] === av ? `${path.color}33` : "transparent",
                          border: `2px solid ${avatars[i] === av ? path.color : "transparent"}`,
                          borderRadius: 8, padding: 4, cursor: "pointer",
                          display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                        }}
                      >
                        <img src={av} alt={avatarName(av)} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
                        <span style={{ fontSize: 8, color: C.light }}>{avatarName(av)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── STEP 1: Facility map ── */}
      {step === 1 && (
        <div style={{ flex: 1, overflow: "auto", padding: 18 }}>
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: 2, marginBottom: 4 }}>
            YOUR ROUTE THROUGH THE FACILITY
          </div>
          <div style={{ fontSize: 13, color: C.light, marginBottom: 18 }}>
            6 obstacles · complete them all to finish the mission
          </div>

          {/* Snake map — Row 1: CPs 1-2-3 left to right */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            {checkpoints.slice(0, 3).map((cp, i) => (
              <div key={cp.id} style={{ flex: 1, display: "flex", alignItems: "center" }}>
                <div style={{
                  flex: 1, background: C.card, borderRadius: 10, padding: "12px 8px",
                  border: `2px solid ${i === 0 ? path.color : C.dim}`,
                  textAlign: "center",
                }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%",
                    background: path.color, margin: "0 auto 6px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800, color: "#000000",
                  }}>{cp.id}</div>
                  <div style={{ fontSize: 16, marginBottom: 4 }}>{OBSTACLE_EMOJIS[i]}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.white, lineHeight: 1.2 }}>{cp.obstacle}</div>
                  <div style={{ fontSize: 10, color: path.color, fontWeight: 700, marginTop: 4 }}>+{cp.xp} XP</div>
                  {i === 0 && (
                    <div style={{
                      fontSize: 8, color: path.color, fontWeight: 700,
                      marginTop: 4, letterSpacing: 0.5,
                    }}>START HERE</div>
                  )}
                </div>
                {i < 2 && (
                  <div style={{ color: C.muted, fontSize: 30, padding: "0 2px", flexShrink: 0 }}>→</div>
                )}
              </div>
            ))}
          </div>

          {/* Downward connector on right side */}
          <div style={{ display: "flex", justifyContent: "flex-end", paddingRight: 22 }}>
            <div style={{ color: C.muted, fontSize: 30 }}>↓</div>
          </div>

          {/* Row 2: CPs 6-5-4 right to left (reversed display) */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
            {[checkpoints[5], checkpoints[4], checkpoints[3]].map((cp, i) => (
              <div key={cp.id} style={{ flex: 1, display: "flex", alignItems: "center" }}>
                <div style={{
                  flex: 1, background: C.card, borderRadius: 10, padding: "12px 8px",
                  border: `2px solid ${C.dim}`, textAlign: "center",
                }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%",
                    background: path.color, margin: "0 auto 6px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800, color: C.white,
                  }}>{cp.id}</div>
                  <div style={{ fontSize: 16, marginBottom: 4 }}>{OBSTACLE_EMOJIS[cp.id - 1]}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.white, lineHeight: 1.2 }}>{cp.obstacle}</div>
                  <div style={{ fontSize: 10, color: path.color, fontWeight: 700, marginTop: 4 }}>+{cp.xp} XP</div>
                </div>
                {i < 2 && (
                  <div style={{ color: C.muted, fontSize: 30, padding: "0 2px", flexShrink: 0 }}>←</div>
                )}
              </div>
            ))}
          </div>

          <div style={{
            marginTop: 16, background: `${path.color}15`, borderRadius: 8,
            padding: 12, border: `1px solid ${path.color}44`,
            fontSize: 12, color: C.light, lineHeight: 1.5,
          }}>
            🔊 Scan your wristband at each checkpoint to save your progress and unlock the next obstacle.
          </div>
        </div>
      )}

      {/* Navigation footer */}
      <div style={{
        paddingTop: 12, paddingBottom: 12, background: C.navyMid,
        borderTop: `1px solid ${C.dim}`,
        display: "flex", gap: 10, alignItems: "center", flexShrink: 0,
        ...edgeStyle,
      }}>
        {step === 0 && (
          <>
            <button
              onClick={onBack}
              style={{
                padding: "12px 16px", background: "transparent",
                color: C.muted, border: `1px solid ${C.dim}`,
                borderRadius: 8, fontSize: 17, cursor: "pointer",
              }}
            >← Back</button>
            <button
              onClick={() => setStep(1)}
              style={{
                flex: 1, padding: "13px 0", background: path.color, color: "#fff",
                border: "none", borderRadius: 8, fontWeight: 700,
                fontSize: 17, cursor: "pointer", minHeight: 48,
              }}
            >VIEW MAP →</button>
          </>
        )}
        {step === 1 && (
          <>
            <button
              onClick={() => setStep(0)}
              style={{
                padding: "12px 16px", background: "transparent",
                color: C.muted, border: `1px solid ${C.dim}`,
                borderRadius: 8, fontSize: 17, cursor: "pointer",
              }}
            >← Back</button>
            <button
              onClick={onNext}
              style={{
                flex: 1, padding: "13px 0", background: C.pink, color: "#fff",
                border: "none", borderRadius: 8, fontWeight: 700,
                fontSize: 17, cursor: "pointer", minHeight: 48,
              }}
            >START MISSION →</button>
          </>
        )}
      </div>
    </div>
  );
}
